import React from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { alpha, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { hero, TNO_MEDIA_LAB_LOGO_BG } from '../sortilegioContent';
import TnoMediaLabLogo, { MEDIA_LAB_TAGLINES } from 'components/TnoMediaLabLogo';

const HERO_OUTER_PADDING_X = { xs: 2.5, sm: 3.5, md: 4.5 };
const HERO_OUTER_PADDING_Y = { xs: 2, sm: 3, md: 4 };
const PANEL_PADDING_X = { xs: 3, sm: 4, md: 5 };
const PANEL_PADDING_Y = { xs: 3, sm: 4, md: 5 };

const Hero = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <Box
      position="relative"
      overflow="hidden"
      paddingX={HERO_OUTER_PADDING_X}
      paddingY={HERO_OUTER_PADDING_Y}
    >
      <Box
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        sx={{
          background: `
            radial-gradient(circle at 12% 18%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 40%),
            radial-gradient(circle at 88% 8%, ${alpha(theme.palette.secondary.main, 0.16)} 0%, transparent 36%),
            radial-gradient(circle at 70% 85%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 30%),
            linear-gradient(135deg, ${alpha(theme.palette.alternate.main, 0.5)} 0%, transparent 55%)
          `,
          pointerEvents: 'none',
        }}
      />

      <Grid container spacing={{ xs: 3, md: 5 }} alignItems="stretch" position="relative" zIndex={1}>
        <Grid item xs={12} md={7}>
          <Box
            data-aos={isMd ? 'fade-right' : 'fade-up'}
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
          >
            <Box display="flex" flexWrap="wrap" marginBottom={3} style={{ gap: 8 }}>
              {hero.highlights.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  }}
                />
              ))}
            </Box>

            <Typography
              variant="overline"
              color="secondary"
              sx={{ fontWeight: 800, letterSpacing: 1.6, mb: 1.5, display: 'block' }}
            >
              {hero.overline}
            </Typography>

            <Typography
              variant="h1"
              color="textPrimary"
              marginBottom={2}
              sx={{
                fontWeight: 800,
                lineHeight: 1.12,
                fontSize: { xs: '1.85rem', sm: '2.35rem', md: '2.7rem' },
              }}
            >
              {hero.title}
            </Typography>

            <Typography
              sx={{ fontWeight: 700, color: 'primary.main', mb: 2, fontSize: { xs: '1.05rem', md: '1.15rem' } }}
            >
              {hero.client} · {hero.city}
            </Typography>

            <Typography
              variant="h2"
              component="p"
              color="textSecondary"
              marginBottom={4}
              sx={{
                fontWeight: 400,
                lineHeight: 1.75,
                fontSize: { xs: '1.05rem', md: '1.15rem' },
              }}
            >
              {hero.subtitle}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="#inversion"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.6,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '1rem',
                alignSelf: 'flex-start',
              }}
            >
              {hero.cta}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            data-aos={isMd ? 'fade-left' : 'fade-up'}
            height="100%"
            borderRadius={4}
            bgcolor={TNO_MEDIA_LAB_LOGO_BG}
            border={`1px solid ${alpha(theme.palette.primary.main, 0.1)}`}
            display="flex"
            flexDirection="column"
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
              marginBottom={3}
            >
              <TnoMediaLabLogo
                tagline={MEDIA_LAB_TAGLINES.agency}
                alt={hero.brand}
                width={{ xs: 248, md: 320 }}
                height="auto"
              />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, lineHeight: 1.3, textAlign: 'center' }}>
              Brief de la propuesta
            </Typography>

            <Grid container spacing={2} sx={{ flex: 1 }}>
              {hero.snapshot.items.map((item) => (
                <Grid item xs={6} key={item.label} sx={{ display: 'flex' }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, mt: 0.5 }}>{item.value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;
